import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import store from '@/store';

// Handle NavigationDuplicated stack error:
// https://github.com/vuejs/vue-router/issues/2881#issuecomment-520554378
const originalPush = VueRouter.prototype.push;
// @ts-ignore
VueRouter.prototype.push = function push(location, onResolve, onReject) {
    if (onResolve || onReject) {
        return originalPush.call(this, location, onResolve, onReject);
    }
    // @ts-ignore
    return originalPush.call(this, location).catch((err) => err);
};

Vue.use(VueRouter);

const requireAdmin = (to: any, from: any, next: any) => {
    const user = store.getters['auth/user'];
    if (user?.role === 'admin') {
        next();
    } else {
        next({ name: 'dashboard_view' });
    }
};

const routes: Array<RouteConfig> = [
    {
        path: '/login',
        name: 'login_view',
        component: () => import(/* webpackChunkName: "LoginView" */ '../views/Login.view.vue')
    },
    {
        path: '/',
        name: 'main_layout_view',
        // redirect: '/dashboard_view',
        component: () => import(/* webpackChunkName: "MainLayout" */ '../views/MainLayout.view.vue'),
        beforeEnter: async (to: any, from: any, next: any) => {
            // Check if token is set. If not, redirect to login
            if (store.getters['auth/token']) {
                await next();
            } else {
                await next({ name: 'login_view' });
            }
        },
        children: [
            {
                path: '/',
                name: 'dashboard_view',
                component: () => import(/* webpackChunkName: "Dashboard" */ '../views/Dashboard.view.vue')
            },
            {
                path: '/projects',
                name: 'projects_overview',
                component: () => import(/* webpackChunkName: "ProjectsOverview" */ '../views/Project/ProjectsOverview.view.vue')
            },
            {
                path: '/projects/calendar',
                name: 'projects_calendar',
                component: () => import(/* webpackChunkName: "ProjectsCalendar" */ '../views/Project/ProjectsCalendar.view.vue')
            },
            {
                path: '/projects/mine',
                name: 'projects_mine',
                component: () => import(/* webpackChunkName: "MyProjects" */ '../views/Project/MyProjects.view.vue')
            },
            {
                // Kundenliste anzeigen
                path: '/customers',
                name: 'customer_list',
                component: () => import(/* webpackChunkName: "CustomerList" */ '../views/Customer/CustomerList.view.vue'),
                children: [
                    {
                        // Alle Projekte des Kunden anzeigen
                        path: ':customerId',
                        name: 'customer_detail',
                        props: true,
                        component: () => import(/* webpackChunkName: "CustomerDetail" */ '../views/Customer/CustomerDetail.view.vue'),
                        children: [
                            {
                                // Projektliste für einen bestimmten Kunden
                                path: 'project/:projectId',
                                name: 'project_list',
                                props: true,
                                component: () => import(/* webpackChunkName: "ProjectList" */ '../views/Project/ProjectList.view.vue'),
                                children: [
                                    {
                                        // Projektdetails-Seite -> alle Actuators anzeigen
                                        path: 'actuators',
                                        name: 'actuators_view',
                                        component: () => import(/* webpackChunkName: "ActuatorsView" */ '../views/Actuators.view.vue')
                                    }
                                ]
                            }
                        ]
                    }
                ]
            },
            {
                path: '/customers/:customerId/project/:projectId/actuators/:id',
                name: 'actuator_detail_view',
                props: true,
                component: () => import(/* webpackChunkName: "BlueprintDetailView" */ '../views/ActuatorDetail.view.vue')
            },
            {
                path: '/settings',
                name: 'settings_view',
                beforeEnter: requireAdmin,
                redirect: { name: 'settings_user_management' },
                component: () => import(/* webpackChunkName: "SettingsView" */ '../views/Settings/Settings.view.vue'),
                children: [
                    {
                        path: 'users/management',
                        name: 'settings_user_management',
                        component: () => import(/* webpackChunkName: "SettingsUserManagement" */ '../views/Settings/UserManagement.view.vue')
                    },
                    {
                        path: 'users/permissions',
                        name: 'settings_permissions',
                        component: () => import(/* webpackChunkName: "SettingsPermissions" */ '../views/Settings/Permissions.view.vue')
                    },
                    {
                        path: 'number-ranges/customer',
                        name: 'settings_number_range_customer',
                        props: { entity: 'customer' },
                        component: () => import(/* webpackChunkName: "SettingsNumberRange" */ '../views/Settings/NumberRange.view.vue')
                    },
                    {
                        path: 'number-ranges/project',
                        name: 'settings_number_range_project',
                        props: { entity: 'project' },
                        component: () => import(/* webpackChunkName: "SettingsNumberRange" */ '../views/Settings/NumberRange.view.vue')
                    }
                ]
            },
            {
                path: '/reports/overview',
                name: 'reports_view',
                beforeEnter: requireAdmin,
                component: () => import(/* webpackChunkName: "ReportsOverview" */ '../views/Reports/Overview.view.vue')
            },
            {
                path: '/reports/actuator-labels',
                name: 'actuator-labels_view',
                beforeEnter: requireAdmin,
                component: () => import(/* webpackChunkName: "ActuatorLabelsView" */ '../views/Reports/ActuatorLabel.view.vue')
            },
            {
                path: '/reports/actuators',
                name: 'reports_actuators_view',
                beforeEnter: requireAdmin,
                component: () => import(/* webpackChunkName: "ReportsActuators" */ '../views/Reports/Actuators.view.vue')
            },
            {
                path: '/reports/projects',
                name: 'reports_projects_view',
                beforeEnter: requireAdmin,
                component: () => import(/* webpackChunkName: "ReportsProjectsShell" */ '../views/Reports/ProjectsShell.view.vue'),
                redirect: { name: 'reports_projects_zahlen' },
                children: [
                    {
                        path: 'zahlen',
                        name: 'reports_projects_zahlen',
                        component: () => import(/* webpackChunkName: "ReportsProjectsNumbers" */ '../views/Reports/ProjectsNumbers.view.vue')
                    },
                    {
                        path: 'projektdauer',
                        name: 'reports_projects_duration',
                        component: () => import(/* webpackChunkName: "ReportsProjectsDuration" */ '../views/Reports/ProjectsDuration.view.vue')
                    }
                ]
            },
            {
                path: '/reports/customers',
                name: 'reports_customers_view',
                beforeEnter: requireAdmin,
                component: () => import(/* webpackChunkName: "ReportsCustomersShell" */ '../views/Reports/CustomersShell.view.vue'),
                redirect: { name: 'reports_customers_overview' },
                children: [
                    {
                        path: '',
                        name: 'reports_customers_overview',
                        component: () => import(/* webpackChunkName: "ReportsCustomersOverview" */ '../views/Reports/CustomersOverview.view.vue')
                    },
                    {
                        path: 'kunden',
                        name: 'reports_customers_directory',
                        component: () => import(
                            /* webpackChunkName: "ReportsCustomersDirectory", webpackPrefetch: false, webpackPreload: false */
                            '../views/Reports/CustomersDirectory.view.vue'
                        )
                    },
                    {
                        path: 'kunden/:customerId',
                        name: 'reports_customers_detail',
                        props: true,
                        component: () => import(
                            /* webpackChunkName: "ReportsCustomersDetail", webpackPrefetch: false, webpackPreload: false */
                            '../views/Reports/CustomersDirectoryDetail.view.vue'
                        )
                    }
                ]
            }
        ]
    },
    {
        path: '*',
        component: () => import(/* webpackChunkName: "LoginView" */ '../views/Login.view.vue')
    }
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
});

export default router;
